import { Alert, Button } from '@design-system';
import { useTranslation } from 'next-i18next';
import { ReactNode } from 'react';

import { DetailsFormController } from './hooks/useDetailsForm';
import { DetailsAddress, DetailsCard } from './types';
import { AddAddressDialog } from './ui/AddAddressDialog';
import { AddCardDialog } from './ui/AddCardDialog';
import { PaymentCardSection } from './ui/PaymentCardSection';
import { PreOrderOptIn } from './ui/PreOrderOptIn';
import { SelectAddressDialog } from './ui/SelectAddressDialog';
import { SelectCardDialog } from './ui/SelectCardDialog';
import { ShippingAddressSection } from './ui/ShippingAddressSection';

export type DetailsSectionProps = {
  /** Owned by the wrapper so it can select/close after persisting. */
  form: DetailsFormController;
  /** Saved addresses, most-recently-used first. Empty → inline capture form. */
  addresses: DetailsAddress[];
  /** Saved cards, most-recently-used first. Empty → inline capture form. */
  cards: DetailsCard[];
  /**
   * Stripe `AddressElement`. Rendered inline when no address is saved, or
   * inside the "Add new address" dialog otherwise — never both at once.
   */
  addressFields: ReactNode;
  /** Stripe card Elements. Same placement rules as `addressFields`. */
  cardFields: ReactNode;
  /**
   * Stripe billing `AddressElement`, in its own scoped `Elements` group so it
   * never collides with the shipping element. Shown when billing differs from
   * shipping, in whichever card surface is active.
   */
  billingAddressFields?: ReactNode;
  /** Whether the release has pre-orders enabled — gates the opt-in callout entirely. */
  preOrdersEnabled: boolean;
  /** Flows that hand payment off elsewhere hide the card section entirely. */
  showPaymentCard?: boolean;
  showMarketingOptIn?: boolean;
  marketingOptIn: boolean;
  onMarketingOptInChange: (marketingOptIn: boolean) => void;
  /**
   * Gates the Review button. Details cannot see inside the Stripe Elements, so
   * the wrapper owns field validity and passes the result down.
   */
  canSubmit?: boolean;
  isSubmitting?: boolean;
  /** Persistence failure surfaced in whichever surface is active. */
  errorMessage?: string | null;
  /**
   * Add-address dialog state, owned by the wrapper: field-completeness gate,
   * in-flight flag, scoped error, and the persist handler for the address in
   * the Stripe `AddressElement`.
   */
  addAddress: {
    canSave?: boolean;
    isSaving?: boolean;
    errorMessage?: string | null;
    onSave: () => void;
  };
  /** Add-card dialog state, mirroring `addAddress` for the card surface. */
  addCard: {
    canSave?: boolean;
    isSaving?: boolean;
    errorMessage?: string | null;
    onSave: () => void;
  };
  onSubmit: () => void;
};

export const DetailsSection = ({
  addAddress,
  addCard,
  addressFields,
  addresses,
  billingAddressFields,
  canSubmit = true,
  cardFields,
  cards,
  errorMessage,
  form,
  isSubmitting,
  marketingOptIn,
  onMarketingOptInChange,
  onSubmit,
  preOrdersEnabled,
  showMarketingOptIn = false,
  showPaymentCard = true,
}: DetailsSectionProps) => {
  const { t } = useTranslation('checkout');

  const mainError = errorMessage ? (
    <Alert status="error">{errorMessage}</Alert>
  ) : null;

  const addressDialogError = addAddress.errorMessage ? (
    <Alert status="error">{addAddress.errorMessage}</Alert>
  ) : null;

  const cardDialogError = addCard.errorMessage ? (
    <Alert status="error">{addCard.errorMessage}</Alert>
  ) : null;

  return (
    <section className="flex flex-col gap-y-12">
      <ShippingAddressSection
        addressFields={addressFields}
        marketingOptIn={marketingOptIn}
        onEdit={() => form.open('selectAddress')}
        onMarketingOptInChange={onMarketingOptInChange}
        onSaveAddressChange={form.setSaveAddress}
        saveAddress={form.values.saveAddress}
        selectedAddress={form.selectedAddress}
        showMarketingOptIn={showMarketingOptIn}
      />

      {showPaymentCard && (
        <PaymentCardSection
          billingAddressFields={billingAddressFields}
          billingSameAsShipping={form.values.billingSameAsShipping}
          cardFields={cardFields}
          onBillingSameAsShippingChange={form.setBillingSameAsShipping}
          onEdit={() => form.open('selectCard')}
          onSaveCardChange={form.setSaveCard}
          saveCard={form.values.saveCard}
          selectedCard={form.selectedCard}
        />
      )}

      {preOrdersEnabled && (
        <PreOrderOptIn
          checked={form.values.preOrderOptIn}
          onChange={form.setPreOrderOptIn}
        />
      )}

      <div className="flex flex-col gap-3">
        {form.openDialog === null && mainError}

        <Button
          disabled={!canSubmit || isSubmitting}
          fullWidth
          label={t('details.submit')}
          loading={isSubmitting}
          onClick={onSubmit}
          variant="primary"
        />
      </div>

      {form.openDialog === 'selectAddress' && (
        <SelectAddressDialog
          addresses={addresses}
          onAddNew={() => form.open('addAddress')}
          onClose={form.close}
          onSave={(addressId) => {
            form.setSelectedAddressId(addressId);
            form.close();
          }}
          selectedAddressId={form.values.selectedAddressId}
        />
      )}

      {form.openDialog === 'addAddress' && (
        <AddAddressDialog
          addressFields={addressFields}
          canSave={addAddress.canSave}
          error={addressDialogError}
          isSaving={addAddress.isSaving}
          onClose={() => form.open('selectAddress')}
          onSave={addAddress.onSave}
          onSaveAddressChange={form.setSaveAddress}
          saveAddress={form.values.saveAddress}
        />
      )}

      {form.openDialog === 'selectCard' && (
        <SelectCardDialog
          cards={cards}
          onAddNew={() => form.open('addCard')}
          onClose={form.close}
          onSave={(cardId) => {
            form.setSelectedCardId(cardId);
            form.close();
          }}
          selectedCardId={form.values.selectedCardId}
        />
      )}

      {form.openDialog === 'addCard' && (
        <AddCardDialog
          billingAddressFields={billingAddressFields}
          billingSameAsShipping={form.values.billingSameAsShipping}
          canSave={addCard.canSave}
          cardFields={cardFields}
          error={cardDialogError}
          isSaving={addCard.isSaving}
          onBillingSameAsShippingChange={form.setBillingSameAsShipping}
          onClose={() => form.open('selectCard')}
          onSave={addCard.onSave}
          onSaveCardChange={form.setSaveCard}
          saveCard={form.values.saveCard}
        />
      )}
    </section>
  );
};

export default DetailsSection;
